import { createClient, type SupabaseClient } from "@supabase/supabase-js";

// The Supabase keys are injected at build time by the GitHub Pages
// deploy workflow from the repo secrets VITE_SUPABASE_URL and
// VITE_SUPABASE_ANON_KEY. At dev time they may be absent; we log a
// clear warning instead of crashing so the rest of the app keeps
// working against IndexedDB while the cloud pieces are being built.
const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export const supabaseConfigured = Boolean(url && anonKey);

if (!supabaseConfigured) {
  // eslint-disable-next-line no-console
  console.warn(
    "Supabase env vars not configured. Cloud features disabled. Set " +
    "VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY as GitHub Actions secrets.",
  );
}

// When unconfigured we still export a client against a dummy URL so
// `import { supabase } from ...` never throws at module load. Any
// actual method call will fail loudly with a network error.
export const supabase: SupabaseClient = createClient(
  url ?? "https://example.invalid",
  anonKey ?? "missing-anon-key",
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      // We redeem invite / recovery URLs ourselves in
      // pages/AcceptInvite.tsx instead of letting supabase-js do
      // it via detectSessionInUrl. The auto-redeem was racing our
      // explicit exchange — when the SDK got there first, our
      // call hit "Error confirming user" because the one-time
      // token had already been consumed, leaving the form
      // stuck even though the SDK actually had set up the
      // session. Owning the redeem path keeps every URL variant
      // (#access_token=…, ?code=…, ?token_hash=…) in one place
      // and avoids the double-exchange.
      detectSessionInUrl: false,
      // Bypass `navigator.locks` so browser extensions that hijack or
      // block the Web Locks API (SES-based lockdown scripts, some
      // wallet extensions) can't stall signInWithPassword() after a
      // successful HTTP response. The session storage is single-tab
      // localStorage in our case; serialising writes isn't critical.
      lock: async (_name, _timeout, fn) => fn(),
    },
  },
);

// Snapshot the URL hash AND query at module load. supabase-js will
// consume them asynchronously once detectSessionInUrl fires; we
// read them now so App.tsx can decide whether to route to
// AcceptInvite before the parser has cleared them — and so the
// AcceptInvite component can replay the original params through
// the explicit setSession / exchangeCodeForSession / verifyOtp
// helpers if detectSessionInUrl's own attempt didn't take.
export const initialAuthHash =
  typeof window !== "undefined" ? window.location.hash : "";
export const initialAuthSearch =
  typeof window !== "undefined" ? window.location.search : "";
const initialHash = initialAuthHash;
const initialSearch = initialAuthSearch;

export function isInviteFlow(): boolean {
  // Custom scanner-resistant invite flow: hash route carrying our own
  // UUID token (minted by send-invite, redeemed by redeem-invite). No
  // Supabase auth token in the URL → email scanners can't burn it.
  // URL shape: https://.../#/accept-invite?invite=<UUID>
  if (initialHash.includes("/accept-invite?invite=")) return true;

  // Implicit (hash) flow: Supabase appends `#access_token=…&type=…`
  // to SITE_URL on its /verify redirect.
  if (
    initialHash.includes("type=invite") ||
    initialHash.includes("type=recovery") ||
    initialHash.includes("type=magiclink")
  ) return true;
  // PKCE (query) flow: Supabase appends `?code=…` instead. The
  // code can be an invite, password recovery, or OAuth callback —
  // all of which should land on AcceptInvite so the user can set
  // (or reset) a password before being dropped into the app. The
  // `token_hash=` fallback covers the OTP / email-link variant.
  if (
    initialSearch.includes("code=") ||
    initialSearch.includes("token_hash=")
  ) return true;
  // Error redirect: Supabase /verify failed (expired link, link
  // already consumed by an email security scanner, invalid token,
  // …). Land on AcceptInvite so the user sees the error message
  // instead of being silently routed to Login.
  return (
    initialSearch.includes("error=") ||
    initialSearch.includes("error_description=") ||
    initialHash.includes("error=") ||
    initialHash.includes("error_description=")
  );
}
